// src/components/FirstNamePrompt.js
import React, { useEffect, useState } from "react";
import { standardUserApi } from "../services/standardUserApi";
import { colors, radii, shadow, buttonPrimary, primaryHover } from "../styles/designTokens";

/**
 * One-time prompt for a subscriber's first name, shown on the page they
 * land on right after subscribing. Signup itself (CreateAccount.js) only
 * collects email/password - it deliberately sends firstName: "" to keep
 * that form frictionless - so users.first_name can be blank for a real,
 * paying subscriber. That's a problem specifically for the connections
 * feature (Connections/Circles/Stay In Touch all display otherUser's
 * first/last name), so this asks for it once subscribing is already done,
 * rather than adding a field to the signup form itself.
 */
const FirstNamePrompt = () => {
  const [show, setShow] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const check = async () => {
      const token = localStorage.getItem("memberstackToken");
      if (!token) return;
      try {
        const profile = await standardUserApi.getUserProfile(token);
        if (!profile.firstName || !profile.firstName.trim()) {
          setShow(true);
        }
      } catch (err) {
        console.error("FirstNamePrompt › getUserProfile error:", err);
      }
    };
    check();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim()) return;
    const token = localStorage.getItem("memberstackToken");
    setSubmitting(true);
    setErrorMessage("");
    try {
      await standardUserApi.updateUserProfile(token, { firstName: firstName.trim() });
      setShow(false);
    } catch (err) {
      console.error("FirstNamePrompt › update error:", err);
      setErrorMessage("Couldn't save that right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!show || dismissed) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0, left: 0,
        width: "100%", height: "100%",
        backgroundColor: "rgba(17, 24, 39, 0.55)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "100px",
        zIndex: 1300,
      }}
    >
      <div
        style={{
          background: colors.surface,
          borderRadius: radii.lg,
          padding: "32px 28px",
          textAlign: "center",
          maxWidth: "380px",
          width: "88%",
          boxShadow: shadow.card,
        }}
      >
        <p style={{ fontSize: "1.8rem", marginBottom: "8px" }} aria-hidden="true">👋</p>
        <h3 style={{ color: colors.ink, fontSize: "1.2rem", fontWeight: "800", marginBottom: "8px" }}>
          What's your first name?
        </h3>
        <p style={{ color: colors.muted, fontSize: "0.9rem", lineHeight: 1.55, marginBottom: "20px" }}>
          So people you meet and connect with on OkGather can actually recognize who you are.
        </p>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            autoFocus
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "12px 16px",
              borderRadius: radii.sm,
              border: `1.5px solid ${colors.border}`,
              fontSize: "0.95rem",
              marginBottom: "14px",
              textAlign: "center",
            }}
          />
          {errorMessage && <p style={{ color: colors.danger, fontSize: "0.85rem", marginBottom: "12px" }}>{errorMessage}</p>}
          <button
            type="submit"
            disabled={submitting || !firstName.trim()}
            style={{
              ...buttonPrimary,
              width: "100%",
              opacity: submitting || !firstName.trim() ? 0.6 : 1,
              cursor: submitting || !firstName.trim() ? "default" : "pointer",
            }}
            onMouseEnter={submitting || !firstName.trim() ? undefined : primaryHover.onMouseEnter}
            onMouseLeave={submitting || !firstName.trim() ? undefined : primaryHover.onMouseLeave}
          >
            {submitting ? "Saving..." : "Save"}
          </button>
        </form>
        <button
          onClick={() => setDismissed(true)}
          style={{
            background: "none", border: "none", color: colors.faint, fontSize: "0.8rem",
            marginTop: "14px", cursor: "pointer", textDecoration: "underline",
          }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
};

export default FirstNamePrompt;
